import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { gsap } from 'gsap';

import { scroll_inst } from '../../utils';

const TARGET_SELECTOR = ['.single-image', '.text-image-image', '.team-member-image'].join(',');
const EXCLUDED_ANCESTORS = ['.artwork', '.logo', '.footer--logo', '.footer-bottom-logo', '.navigation-menu-image'];
const clamp = gsap.utils.clamp(-1800, 1800);
const WORK_PATH_PATTERN = /^\/OurWork(?:\/[^/]+)?\/?$/i;

function isIOSWebKit() {
    const ua = navigator.userAgent || '';
    const isIOSDevice = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isWebKitEngine = /WebKit/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);

    return isIOSDevice && isWebKitEngine;
}

function getNativeScrollY() {
    if (typeof window.scrollY === 'number') return window.scrollY;
    if (typeof window.pageYOffset === 'number') return window.pageYOffset;
    if (typeof document.documentElement?.scrollTop === 'number') return document.documentElement.scrollTop;
    if (typeof document.body?.scrollTop === 'number') return document.body.scrollTop;
    return 0;
}

function getOverlayViewportScrollY() {
    if (!scroll_inst?.getElements) return null;

    try {
        const viewport = scroll_inst.getElements().viewport;
        return typeof viewport?.scrollTop === 'number' ? viewport.scrollTop : null;
    } catch {
        return null;
    }
}

function getOverlayInstanceScrollY() {
    if (!scroll_inst?.scroll) return null;

    try {
        const info = scroll_inst.scroll();
        return Number.isFinite(info?.position?.y) ? info.position.y : null;
    } catch {
        return null;
    }
}

function getScrollY() {
    const candidates = [getOverlayInstanceScrollY(), getOverlayViewportScrollY(), getNativeScrollY()].filter((value) =>
        Number.isFinite(value)
    );

    return candidates.length > 0 ? Math.max(...candidates, 0) : 0;
}

function shouldSkip(surface) {
    return EXCLUDED_ANCESTORS.some((selector) => surface.closest(selector));
}

function bindSurface(surface) {
    if (!surface || shouldSkip(surface)) return () => {};

    const image = surface.querySelector('img');
    if (!image) return () => {};

    surface.classList.add('scroll-anaglyph-surface');

    const redGhost = image.cloneNode(true);
    redGhost.classList.add('scroll-anaglyph-ghost', 'scroll-anaglyph-ghost-red');
    redGhost.setAttribute('aria-hidden', 'true');

    const cyanGhost = image.cloneNode(true);
    cyanGhost.classList.add('scroll-anaglyph-ghost', 'scroll-anaglyph-ghost-cyan');
    cyanGhost.setAttribute('aria-hidden', 'true');

    const overlay = surface.querySelector('.team-member-social');

    if (overlay) {
        surface.insertBefore(cyanGhost, overlay);
        surface.insertBefore(redGhost, overlay);
    } else {
        surface.appendChild(cyanGhost);
        surface.appendChild(redGhost);
    }

    return () => {
        surface.classList.remove('scroll-anaglyph-surface');
        redGhost.remove();
        cyanGhost.remove();
        surface.style.removeProperty('--anaglyph-x');
        surface.style.removeProperty('--anaglyph-y');
        surface.style.removeProperty('--anaglyph-opacity');
    };
}

function ImageAnaglyphEffects() {
    const { pathname } = useLocation();

    useEffect(() => {
        if (WORK_PATH_PATTERN.test(pathname)) {
            return undefined;
        }
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return undefined;
        }
        if (window.matchMedia('(max-width: 991px)').matches) {
            return undefined;
        }
        // iOS WebKit can intermittently blank layers with blend/filter + ticker effects.
        if (isIOSWebKit()) {
            return undefined;
        }

        const surfaces = Array.from(document.querySelectorAll(TARGET_SELECTOR));
        const cleanups = surfaces.map(bindSurface);

        let lastY = getScrollY();
        let lastTime = performance.now();
        const state = { x: 0, y: 0, opacity: 0 };

        const tick = () => {
            const now = performance.now();
            const currentY = getScrollY();
            const dt = Math.max((now - lastTime) * 0.001, 1 / 120);
            const velocity = clamp((currentY - lastY) / dt);
            const strength = Math.min(0.72, Math.abs(velocity) / 1400);
            const direction = Math.sign(velocity) || (state.x >= 0 ? 1 : -1);

            const targetX = direction * 3.2 * strength;
            const targetY = direction * 9 * strength;
            const targetOpacity = 0.03 + strength * 0.18;

            state.x += (targetX - state.x) * 0.12;
            state.y += (targetY - state.y) * 0.12;
            state.opacity += ((strength > 0.01 ? targetOpacity : 0) - state.opacity) * 0.1;

            surfaces.forEach((surface) => {
                if (!surface.isConnected || !surface.classList.contains('scroll-anaglyph-surface')) return;
                surface.style.setProperty('--anaglyph-x', `${state.x.toFixed(2)}px`);
                surface.style.setProperty('--anaglyph-y', `${state.y.toFixed(2)}px`);
                surface.style.setProperty('--anaglyph-opacity', state.opacity.toFixed(3));
            });

            lastY = currentY;
            lastTime = now;
        };

        gsap.ticker.add(tick);

        return () => {
            gsap.ticker.remove(tick);
            cleanups.forEach((cleanup) => cleanup());
        };
    }, [pathname]);

    return null;
}

export default ImageAnaglyphEffects;
